import {useQuery} from '@tanstack/react-query';
import {ArrowLeft, ExternalLink, ShieldCheck} from 'lucide-react';
import {loadRuntimeConfig} from '../api';
import {Surface} from '../design';
import type {PublicOperatorConfig} from '../runtimeConfig';

export const PrivacyPage = ({operator}: {operator: PublicOperatorConfig}) => (
  <main className="pd-privacy-page" id="pursuitdeck-main">
    <a className="pd-privacy-back" href="/">
      <ArrowLeft size={16} aria-hidden="true" />
      Back to PursuitDeck
    </a>
    <header className="pd-privacy-hero">
      <span className="pd-eyebrow">Privacy and data handling</span>
      <ShieldCheck size={38} aria-hidden="true" />
      <h1>Your work stays under your control.</h1>
      <p>
        PursuitDeck is a private job-search workspace. This page explains who can access its data,
        when configured services receive it, and what deletion does.
      </p>
    </header>

    <div className="pd-privacy-grid">
      <Surface>
        <h2>Who can access your workspace</h2>
        <p>
          You and the deployment operator can access stored content. The operator may inspect it
          when necessary to run the service, investigate a failure, recover data, or provide support.
        </p>
      </Surface>
      <Surface>
        <h2>When data leaves PursuitDeck</h2>
        <p>
          When you approve a workflow, it may send selected content to services configured by the operator,
          such as Google, OpenAI, or Apify. The service may send privacy-minimized operator alerts through
          Twilio Email. It sends candidate reminder email only after you explicitly enable reminders in Settings,
          using your selected verified sign-in address; reminders default to disabled and can be disabled again
          at any time. Which services are enabled depends on this deployment.
        </p>
      </Surface>
      <Surface>
        <h2>Applications remain candidate-controlled</h2>
        <p>
          PursuitDeck can inspect, prepare, and fill application material after approval. It does not
          make a final external submission automatically; you remain responsible for the final review and action.
        </p>
      </Surface>
      <Surface>
        <h2>Retention and deletion</h2>
        <p>
          Workspace records are retained until an explicit deletion request. Disconnecting Google removes
          its refresh credential but does not remove imported records. Workspace deletion removes active
          candidate records and objects but keeps a minimal deletion fence that prevents delayed work from
          recreating them. Bounded backups, object versions, and provider-side records can remain until their
          separate retention periods end. Deletion does not remove your Firebase sign-in record, Gmail or
          Calendar content, or accounts held by external providers.
        </p>
        <p>
          During the private beta, Google may expire Gmail or Calendar authorization after seven days.
          PursuitDeck stores the deadline, warns before it, stops dependent synchronization when it expires,
          and asks you to reconnect. It never treats an expired grant as usable.
        </p>
      </Surface>
    </div>

    <Surface className="pd-privacy-contact">
      <div>
        <span className="pd-eyebrow">Questions, support, or deletion</span>
        <h2>Contact {operator.displayName}</h2>
        <p>This deployment’s operator is the escalation route for privacy requests and account support.</p>
      </div>
      <a className="pd-privacy-contact-link" href={operator.privacyContactUrl}>
        Contact the operator
        {operator.privacyContactUrl.startsWith('https:') ? <ExternalLink size={16} aria-hidden="true" /> : null}
      </a>
    </Surface>
  </main>
);

export const PrivacyRoutePage = () => {
  const config = useQuery({
    queryKey: ['public-runtime-config'],
    queryFn: loadRuntimeConfig,
    staleTime: Infinity,
    retry: false,
  });

  if (config.isLoading) {
    return (
      <main className="pd-state-page" aria-busy="true">
        <div className="pd-loading-mark" aria-hidden="true">PD</div>
        <p>Opening privacy details…</p>
      </main>
    );
  }
  if (!config.data) {
    return (
      <main className="pd-state-page">
        <Surface className="pd-state-card">
          <h1>Privacy details are unavailable.</h1>
          <p>The public deployment configuration could not be validated. Contact the site operator directly.</p>
          <a className="pd-text-link" href="/">Return to PursuitDeck</a>
        </Surface>
      </main>
    );
  }
  return <PrivacyPage operator={config.data.operator} />;
};
